<template>
    <nav class="mobile-tabbar" aria-label="Hauptnavigation">
        <button
            type="button"
            class="tab-btn"
            :class="{ active: isHome }"
            :aria-current="isHome ? 'page' : undefined"
            title="Home"
            aria-label="Home"
            @click="goHome"
        >
            <font-awesome-icon class="tab-icon" icon="fa-home" />
            <span class="tab-label">Home</span>
        </button>
        <button
            type="button"
            class="tab-btn"
            :class="{ active: showInfo }"
            title="App-Info"
            aria-label="App-Info"
            :aria-expanded="showInfo ? 'true' : 'false'"
            @click="openInfo"
        >
            <font-awesome-icon class="tab-icon" icon="fa-circle-info" />
            <span class="tab-label">Info</span>
        </button>
        <button
            type="button"
            class="tab-btn"
            title="Ausloggen"
            aria-label="Ausloggen"
            @click="logOut"
        >
            <font-awesome-icon class="tab-icon" icon="fa-arrow-right-from-bracket" />
            <span class="tab-label">Ausloggen</span>
        </button>
    </nav>
    <div
        v-if="showInfo"
        class="info-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-info-title"
        @click.self="closeInfo"
    >
        <div class="info-card">
            <h2 id="app-info-title">App-Info</h2>
            <dl class="info-list">
                <div class="info-row">
                    <dt>Version</dt>
                    <dd>{{ appVersion }}</dd>
                </div>
                <div v-if="appBuild" class="info-row">
                    <dt>Build</dt>
                    <dd>{{ appBuild }}</dd>
                </div>
                <div v-if="showGitStand" class="info-row">
                    <dt>Stand</dt>
                    <dd>{{ gitCommit }}</dd>
                </div>
                <div class="info-row">
                    <dt>Benutzer</dt>
                    <dd>{{ userName }}</dd>
                </div>
            </dl>
            <button type="button" class="info-close" @click="closeInfo">
                Schließen
            </button>
        </div>
    </div>
    <confirm-dialogue ref="confirmDialogue" />
</template>

<script>
import ConfirmDialogue from './ConfirmDialogue.vue';
import { getClientIdentity } from '@/utils/clientIdentity';
import { isNativeApp } from '@/utils/platform';

export default {
    name: 'TheMobileTabBar',
    components: { ConfirmDialogue },
    data() {
        return {
            showInfo: false,
        };
    },
    computed: {
        isHome() {
            return this.$route.name === 'home';
        },
        identity() {
            return getClientIdentity();
        },
        gitCommit() {
            return process.env.VUE_APP_GIT_COMMIT || 'dev';
        },
        userName() {
            return this.$store.state.auth.user?.login || '—';
        },
        appVersion() {
            if (isNativeApp() && this.identity.version) {
                return this.identity.version;
            }
            return this.gitCommit;
        },
        appBuild() {
            if (!isNativeApp()) {
                return '';
            }
            return this.identity.build || '';
        },
        showGitStand() {
            return isNativeApp() && this.gitCommit && this.gitCommit !== this.appVersion;
        },
    },
    methods: {
        goHome() {
            this.closeInfo();
            if (this.isHome) {
                return;
            }
            this.$router.push({ name: 'home' });
        },
        openInfo() {
            this.showInfo = true;
        },
        closeInfo() {
            this.showInfo = false;
        },
        logOut() {
            this.closeInfo();
            this.confirmAndLogOut();
        },
        async confirmAndLogOut() {
            const ok = await this.$refs.confirmDialogue.show({
                title: 'Ausloggen',
                message: 'Möchten Sie sich wirklich abmelden?',
                okButton: 'Ausloggen',
                cancelButton: 'Abbrechen',
            });
            if (!ok) {
                return;
            }
            this.$store.dispatch('auth/logout');
            this.$router.push('/login');
        },
    },
};
</script>

<style scoped>
.mobile-tabbar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1030;
    display: flex;
    align-items: stretch;
    min-height: var(--app-footer-height);
    padding: 0.35rem 0.15rem calc(0.3rem + env(safe-area-inset-bottom, 0px));
    background-color: #264271;
    color: #fff;
    box-shadow: 0 -1px 0 rgba(255, 255, 255, 0.08);
}

.tab-btn {
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.18rem;
    margin: 0;
    padding: 0.2rem 0.2rem 0.1rem;
    border: none;
    background: transparent;
    color: #c5d0e3;
    font-size: 0.68rem;
    line-height: 1.1;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    user-select: none;
}

.tab-btn.active {
    color: #fff;
}

.tab-icon {
    font-size: 1.15rem;
}

.tab-label {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.info-overlay {
    position: fixed;
    z-index: 2000;
    inset: 0;
    background: rgba(13, 27, 48, 0.72);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
}

.info-card {
    width: min(22rem, 100%);
    background: #1c335a;
    color: #fff;
    border-radius: 16px;
    padding: 1.35rem 1.25rem 1.15rem;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
}

.info-card h2 {
    margin: 0;
    font-size: 1.2rem;
    line-height: 1.3;
    text-align: center;
}

.info-list {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
}

.info-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 0.75rem;
    padding: 0.35rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.info-row:last-child {
    border-bottom: none;
}

.info-row dt {
    margin: 0;
    color: #c5d0e3;
    font-size: 0.85rem;
    font-weight: 400;
}

.info-row dd {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    text-align: right;
    word-break: break-all;
}

.info-close {
    width: 100%;
    border: 2px solid #fff;
    border-radius: 10px;
    background: #fff;
    color: #1c335a;
    padding: 0.65rem 1rem;
    font-size: 1rem;
    cursor: pointer;
}
</style>
